import React, { useCallback, useState, useMemo, useEffect } from "react";
import Input from "../../../components/Atomos/Inputs/Input";
import DropDownList from "../../../components/Atomos/DropDown/Dropdown";
import CalendarInput from "../../../components/Atomos/Inputs/CalendarInput";
import TimeInput from "../../../components/Atomos/Inputs/TimeInput";
import RadioButton from "../../../components/Atomos/Radio/RadioButton";
import WSGCheckbox from "../../../components/Atomos/Checkbox/WSGCheckBox";
import RichTextEditor from "../../../components/SlateEditor/RichTextEditor";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import Trash from "../../../components/Atomos/Icons/EditMode/Trash";
import PlusIcon from "../../../components/Atomos/Icons/PlusIcon";
import { SpeakerTypeEnum } from "../../../Enums/EventEnums";
import { useSessionForm } from "../../../hooks/Events/useSessionForm";
import PaginatedTable from "../../../components/Organismos/Table/PaginatedTable";
import { defaultPageSize } from "../../../utils/const";
import { ISorting } from "../../../interfaces/Components/ITable";
import { toastError } from "../../../utils/toast";
import { fetchPersonByFirm } from "../../../Services/Person";
import { getGuestSpeakersByCompany } from "../../../Services/GuestSpeaker";
import { useProgramDayVenue } from "../../../hooks/Events/useProgramDayVenue";
import { IProgram } from "../../../interfaces/models/IEvent";

interface SessionTabProps {
  eventId: string;
  sessionForm: ReturnType<typeof useSessionForm>["sessionForm"];
  updateSessionField: ReturnType<typeof useSessionForm>["updateSessionField"];
  handleUpdatePresenter: ReturnType<
    typeof useSessionForm
  >["handleUpdatePresenter"];
  handleRemovePresenter: ReturnType<
    typeof useSessionForm
  >["handleRemovePresenter"];
  sessionTypeOptions: { id: string; name: string }[];
  speakerTypeOptions: { key: string; value: string }[];
  firmOptions: { id: string; name: string }[];
  companyOptions: { id: string; name: string }[];
  eventStartDate?: number;
  eventEndDate?: number;
  editingSession?: Pick<IProgram, "title" | "startTime" | "endTime"> | null;
  validationErrors?: {
    sessionTitle?: string;
    sessionDate?: string;
    startTime?: string;
    endTime?: string;
    registrationPageSessionTitle?: string;
  };
  onFieldChange?: (field: string) => void;
}

const useFilteredProfessionals = (selectedFirmId: string | null) => {
  const [filteredProfessionals, setFilteredProfessionals] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    const fetchProfessionalsByFirm = async () => {
      if (!selectedFirmId) {
        setFilteredProfessionals([]);
        return;
      }

      try {
        const persons = await fetchPersonByFirm(selectedFirmId);
        const formattedData = persons.map((person: any) => ({
          id: person.id,
          name:
            person.displayName ||
            `${person.name || ""} ${person.lastName || ""}`.trim() ||
            "Unknown",
        }));
        setFilteredProfessionals(formattedData);
      } catch (error) {
        console.error("Error fetching professionals by firm:", error);
        setFilteredProfessionals([]);
      }
    };

    fetchProfessionalsByFirm();
  }, [selectedFirmId]);

  return filteredProfessionals;
};

const useFilteredGuestSpeakers = (selectedCompanyId: string | null) => {
  const [filteredGuestSpeakers, setFilteredGuestSpeakers] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    const fetchGuestSpeakersByCompany = async () => {
      if (!selectedCompanyId) {
        setFilteredGuestSpeakers([]);
        return;
      }

      try {
        const guestSpeakers =
          await getGuestSpeakersByCompany(selectedCompanyId);
        const formattedData = guestSpeakers.map((guestSpeaker: any) => ({
          id: guestSpeaker.id,
          name:
            `${guestSpeaker.firstName || ""} ${guestSpeaker.lastName || ""}`.trim() ||
            "Unknown",
        }));
        setFilteredGuestSpeakers(formattedData);
      } catch (error) {
        console.error("Error fetching guest speakers by company:", error);
        setFilteredGuestSpeakers([]);
      }
    };

    fetchGuestSpeakersByCompany();
  }, [selectedCompanyId]);

  return filteredGuestSpeakers;
};

const SessionTab: React.FC<SessionTabProps> = ({
  eventId,
  sessionForm,
  updateSessionField,
  handleUpdatePresenter,
  handleRemovePresenter,
  sessionTypeOptions,
  speakerTypeOptions,
  firmOptions,
  companyOptions,
  eventStartDate,
  eventEndDate,
  editingSession,
  validationErrors = {},
  onFieldChange,
}) => {
  const {
    definesDayVenue,
    heldInDifferentLocation,
    handleDifferentLocationToggle,
    venueFieldsDisabled,
  } = useProgramDayVenue({
    eventId,
    eventStartDate,
    sessionDate: sessionForm.sessionDate,
    startTime: sessionForm.startTime,
    venue: sessionForm.venue,
    location: sessionForm.location,
    editingSession,
    updateSessionField,
  });

  const handleStartTimeChange = useCallback(
    (time24: string) => {
      updateSessionField("startTime", time24);
    },
    [updateSessionField],
  );

  const handleEndTimeChange = useCallback(
    (time24: string) => {
      updateSessionField("endTime", time24);
    },
    [updateSessionField],
  );

  const [sort, setSort] = useState<ISorting | undefined>(undefined);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  type PresenterSelectionSource = "memberFirm" | "company" | null;

  const [formPresenter, setFormPresenter] = useState<{
    personType: keyof typeof SpeakerTypeEnum | null;
    memberFirm: { id: string; name: string } | null;
    company: { id: string; name: string } | null;
    professional: { id: string; name: string } | null;
    selectionSource: PresenterSelectionSource;
  }>({
    personType: "speaker" as keyof typeof SpeakerTypeEnum,
    memberFirm: null,
    company: null,
    professional: null,
    selectionSource: null,
  });

  const [dropdownResetKey, setDropdownResetKey] = useState(0);

  const isGuestPresenterType = formPresenter.personType === "guest";
  const showDualSelectionBlocks = !isGuestPresenterType;

  const selectedFirmId =
    showDualSelectionBlocks && formPresenter.selectionSource !== "company"
      ? formPresenter.memberFirm?.id || null
      : null;
  const selectedCompanyId = isGuestPresenterType
    ? formPresenter.memberFirm?.id || null
    : formPresenter.company?.id || null;

  const filteredProfessionals = useFilteredProfessionals(selectedFirmId);
  const filteredGuestSpeakers = useFilteredGuestSpeakers(selectedCompanyId);

  const isMemberFirmBlockDisabled =
    showDualSelectionBlocks && formPresenter.selectionSource === "company";
  const isCompanyBlockDisabled =
    showDualSelectionBlocks && formPresenter.selectionSource === "memberFirm";

  const availableProfessionals = useMemo(() => {
    return filteredProfessionals.filter((professional) => {
      return !sessionForm.presenters.some(
        (presenter) =>
          presenter.professional?.id === professional.id &&
          presenter.personType === formPresenter.personType,
      );
    });
  }, [filteredProfessionals, sessionForm.presenters, formPresenter.personType]);

  const availableGuestSpeakers = useMemo(() => {
    return filteredGuestSpeakers.filter((guestSpeaker) => {
      return !sessionForm.presenters.some(
        (presenter) =>
          presenter.professional?.id === guestSpeaker.id &&
          presenter.personType === formPresenter.personType,
      );
    });
  }, [filteredGuestSpeakers, sessionForm.presenters, formPresenter.personType]);

  const prevFirmIdRef = React.useRef<string | null>(null);
  const prevCompanyIdRef = React.useRef<string | null>(null);
  const prevPersonTypeRef = React.useRef<keyof typeof SpeakerTypeEnum | null>(
    null,
  );

  useEffect(() => {
    if (
      prevFirmIdRef.current !== selectedFirmId &&
      formPresenter.professional &&
      formPresenter.selectionSource !== "company"
    ) {
      setFormPresenter((prev) => ({ ...prev, professional: null }));
    }
    prevFirmIdRef.current = selectedFirmId;
  }, [
    selectedFirmId,
    formPresenter.professional,
    formPresenter.selectionSource,
  ]);

  useEffect(() => {
    if (
      prevCompanyIdRef.current !== selectedCompanyId &&
      formPresenter.professional &&
      (isGuestPresenterType || formPresenter.selectionSource === "company")
    ) {
      setFormPresenter((prev) => ({ ...prev, professional: null }));
    }
    prevCompanyIdRef.current = selectedCompanyId;
  }, [
    selectedCompanyId,
    formPresenter.professional,
    formPresenter.selectionSource,
    isGuestPresenterType,
  ]);

  useEffect(() => {
    if (
      prevPersonTypeRef.current !== null &&
      prevPersonTypeRef.current !== formPresenter.personType
    ) {
      setFormPresenter((prev) => ({
        ...prev,
        memberFirm: null,
        company: null,
        professional: null,
        selectionSource: null,
      }));
      prevFirmIdRef.current = null;
      prevCompanyIdRef.current = null;
      setDropdownResetKey((key) => key + 1);
    }
    prevPersonTypeRef.current = formPresenter.personType;
  }, [formPresenter.personType]);

  const handleMemberFirmBlockOpen = useCallback(
    (open: boolean) => {
      if (!showDualSelectionBlocks) {
        return;
      }

      if (open) {
        setFormPresenter((prev) => ({ ...prev, selectionSource: "memberFirm" }));
        return;
      }

      setFormPresenter((prev) => {
        if (
          prev.selectionSource === "memberFirm" &&
          !prev.memberFirm?.id &&
          !prev.professional
        ) {
          return { ...prev, selectionSource: null };
        }
        return prev;
      });
    },
    [showDualSelectionBlocks],
  );

  const handleCompanyBlockOpen = useCallback(
    (open: boolean) => {
      if (!showDualSelectionBlocks) {
        return;
      }

      if (open) {
        setFormPresenter((prev) => ({ ...prev, selectionSource: "company" }));
        return;
      }

      setFormPresenter((prev) => {
        if (
          prev.selectionSource === "company" &&
          !prev.company?.id &&
          !prev.professional
        ) {
          return { ...prev, selectionSource: null };
        }
        return prev;
      });
    },
    [showDualSelectionBlocks],
  );

  const handleSavePresenter = useCallback(() => {
    if (!formPresenter.professional) {
      toastError("Please select a Professional before saving.");
      return;
    }

    if (!formPresenter.personType) {
      toastError("Please select a presenter type before saving.");
      return;
    }

    const isDuplicate = sessionForm.presenters.some(
      (presenter) =>
        presenter.professional?.id === formPresenter.professional?.id &&
        presenter.personType === formPresenter.personType,
    );

    if (isDuplicate) {
      toastError(
        "This speaker has already been added with the same type. Please select a different speaker or remove the existing one.",
      );
      return;
    }

    const memberFirmForSave = isGuestPresenterType
      ? formPresenter.memberFirm
      : formPresenter.selectionSource === "company"
        ? formPresenter.company
        : formPresenter.memberFirm;

    if (!memberFirmForSave?.id) {
      toastError("Please select a Member Firm or Company before saving.");
      return;
    }

    const professionalSource =
      isGuestPresenterType || formPresenter.selectionSource === "company"
        ? ("guestSpeaker" as const)
        : ("person" as const);

    const newPresenter = {
      personType: formPresenter.personType,
      memberFirm: memberFirmForSave,
      professional: formPresenter.professional,
      professionalSource,
    };
    updateSessionField("presenters", [...sessionForm.presenters, newPresenter]);

    setFormPresenter({
      personType: "speaker" as keyof typeof SpeakerTypeEnum,
      memberFirm: null,
      company: null,
      professional: null,
      selectionSource: null,
    });
    prevFirmIdRef.current = null;
    prevCompanyIdRef.current = null;
    setDropdownResetKey((key) => key + 1);
  }, [formPresenter, updateSessionField, sessionForm.presenters, isGuestPresenterType]);

  const parseName = (
    fullName: string | undefined,
  ): { firstName: string; lastName: string } => {
    if (!fullName) {
      return { firstName: "", lastName: "" };
    }
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: "" };
    }
    const lastName = parts[parts.length - 1];
    const firstName = parts.slice(0, -1).join(" ");
    return { firstName, lastName };
  };

  const formatPresenterData = useMemo(() => {
    const completePresentersWithIndex = sessionForm.presenters
      .map((presenter, originalIndex) => ({ presenter, originalIndex }))
      .filter(
        ({ presenter }) => presenter.professional && presenter.personType,
      );

    return completePresentersWithIndex.map(
      ({ presenter, originalIndex }, index) => {
        const { firstName, lastName } = parseName(presenter.professional?.name);
        return {
          id: originalIndex,
          number: index + 1,
          type:
            speakerTypeOptions.find((opt) => opt.key === presenter.personType)
              ?.value || presenter.personType,
          lastName: lastName || "-",
          firstName: firstName || "-",
          firmCompany: presenter.memberFirm?.name || "-",
          presenter: presenter,
        };
      },
    );
  }, [sessionForm.presenters, speakerTypeOptions]);

  const formatDataWithSorting = useCallback(
    (data: any[]) => {
      if (!sort) {
        return data;
      }

      const sorted = [...data].sort((a, b) => {
        const field = sort.field;
        const order = sort.order;
        let aValue: any = a[field as keyof typeof a];
        let bValue: any = b[field as keyof typeof b];

        if (aValue === "-") {
          aValue = "";
        }
        if (bValue === "-") {
          bValue = "";
        }

        if (aValue === "" && bValue !== "") {
          return order === "asc" ? 1 : -1;
        }
        if (aValue !== "" && bValue === "") {
          return order === "asc" ? -1 : 1;
        }

        if (typeof aValue === "string" && typeof bValue === "string") {
          const comparison = aValue.localeCompare(bValue);
          return order === "asc" ? comparison : -comparison;
        }

        if (typeof aValue === "number" && typeof bValue === "number") {
          return order === "asc" ? aValue - bValue : bValue - aValue;
        }

        return 0;
      });

      return sorted.map((item, index) => ({
        ...item,
        number: index + 1,
      }));
    },
    [sort],
  );

  const columnsDef = useMemo(
    () => [
      {
        id: "number",
        header: "#",
        enableSorting: false,
        cell: ({ row }: { rowIndex: number; row: any; value: any }) => (
          <p>{row.number}</p>
        ),
      },
      {
        id: "type",
        header: "Type",
      },
      {
        id: "lastName",
        header: "Last Name",
      },
      {
        id: "firstName",
        header: "First Name",
      },
      {
        id: "firmCompany",
        header: "Firm / Company",
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        cell: ({ row }: { rowIndex: number; row: any; value: any }) => {
          const index = row.id;
          const handleDelete = (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            console.log("Delete clicked, index from row.original.id:", index);
            console.log("Row original:", row);
            handleRemovePresenter(index);
          };
          return (
            <div
              onClick={handleDelete}
              className="flex items-center gap-2 cursor-pointer text-gray-font"
            >
              <span>Delete</span>
              <div
                className="flex items-center justify-center w-4 h-4"
                style={{ color: "#474F56" }}
              >
                <div className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg>path]:fill-current">
                  <Trash />
                </div>
              </div>
            </div>
          );
        },
      },
    ],
    [handleRemovePresenter],
  );

  return (
    <>
      <div className="flex flex-col gap-6">
        <div>
          <WSGCheckbox
            index={0}
            text="Requires Registration"
            checked={sessionForm.requiresRegistration}
            handleCheckboxChange={() => {
              const newValue = !sessionForm.requiresRegistration;
              updateSessionField("requiresRegistration", newValue);
              if (!newValue) {
                updateSessionField("registrationPageSessionTitle", "");
              }
            }}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            title="Registration Page Session Title"
            name="registrationPageSessionTitle"
            value={sessionForm.registrationPageSessionTitle}
            onChange={(e) => {
              updateSessionField(
                "registrationPageSessionTitle",
                e.target.value,
              );
              onFieldChange?.("registrationPageSessionTitle");
            }}
            error={!!validationErrors.registrationPageSessionTitle}
            errorMsj={validationErrors.registrationPageSessionTitle}
            placeholder=""
            disabled={!sessionForm.requiresRegistration}
            required={sessionForm.requiresRegistration}
          />
          <Input
            title="Session Title"
            name="sessionTitle"
            value={sessionForm.sessionTitle}
            onChange={(e) => {
              updateSessionField("sessionTitle", e.target.value);
              onFieldChange?.("sessionTitle");
            }}
            error={!!validationErrors.sessionTitle}
            errorMsj={validationErrors.sessionTitle}
            placeholder=""
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CalendarInput
            title="Session Dates"
            type="single"
            value={sessionForm.sessionDate}
            onChange={(e) => {
              updateSessionField("sessionDate", e);
              onFieldChange?.("sessionDate");
            }}
            error={!!validationErrors.sessionDate}
            errorMsj={validationErrors.sessionDate}
            placeholder="MM/DD/YYYY"
            minDate={
              eventStartDate ? new Date(eventStartDate * 1000) : undefined
            }
            maxDate={eventEndDate ? new Date(eventEndDate * 1000) : undefined}
            required
          />

          <div className="flex flex-col gap-2 md:gap-[.625rem] w-full">
            <div className="flex gap-2 w-full">
              <label className="text-base md:text-lg font-medium text-gray-font">
                Session Time <span className="text-primary">*</span>
              </label>
            </div>
            <div className="relative w-full grid grid-cols-2 gap-4">
              <div className="[&>div>div:first-child]:hidden">
                <TimeInput
                  title=""
                  value={sessionForm.startTime}
                  onChange={(time) => {
                    handleStartTimeChange(time);
                    onFieldChange?.("startTime");
                  }}
                  placeholder="[00:00 AM / PM]"
                  error={!!validationErrors.startTime}
                  errorMsj={validationErrors.startTime}
                />
              </div>
              <div className="[&>div>div:first-child]:hidden">
                <TimeInput
                  title=""
                  value={sessionForm.endTime}
                  onChange={(time) => {
                    handleEndTimeChange(time);
                    onFieldChange?.("endTime");
                  }}
                  placeholder="[00:00 AM / PM]"
                  error={!!validationErrors.endTime}
                  errorMsj={validationErrors.endTime}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 md:gap-[.625rem] w-full">
            <div className="flex gap-2 w-full">
              <label className="text-base md:text-lg font-medium text-gray-font">
                Format
              </label>
            </div>
            <div className="flex gap-6">
              <RadioButton
                index={0}
                text="24 hour"
                selected={sessionForm.timeFormat === "24"}
                handleRadioButtonChange={() =>
                  updateSessionField("timeFormat", "24")
                }
                name="timeFormat"
              />
              <RadioButton
                index={1}
                text="12 hour"
                selected={sessionForm.timeFormat === "12"}
                handleRadioButtonChange={() =>
                  updateSessionField("timeFormat", "12")
                }
                name="timeFormat"
              />
            </div>
          </div>
        </div>
        {!definesDayVenue && (
          <WSGCheckbox
            index={0}
            text="This session is held in a different location"
            checked={heldInDifferentLocation}
            handleCheckboxChange={handleDifferentLocationToggle}
          />
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            title="Program Venue"
            name="venue"
            value={sessionForm.venue}
            onChange={(e) => updateSessionField("venue", e.target.value)}
            error={false}
            placeholder=""
            disabled={venueFieldsDisabled}
          />

          <Input
            title="Program Venue Location"
            name="location"
            value={sessionForm.location}
            onChange={(e) => updateSessionField("location", e.target.value)}
            error={false}
            placeholder=""
            disabled={venueFieldsDisabled}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-base md:text-lg font-medium text-gray-font font-Roboto">
            Session Description
          </label>
          <div className="[&_p]:text-left [&_div]:text-left [&_*]:text-left">
            <RichTextEditor
              title=""
              name="sessionDescription"
              value={sessionForm.sessionDescription}
              onChange={(e) => {
                const value = (e.target as any).value;
                if (Array.isArray(value)) {
                  updateSessionField("sessionDescription", value);
                }
              }}
              error={false}
              maxChars={3000}
              characterCount={true}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <div className="flex justify-between items-center">
          <h2 className="text-[#0070CD] font-decimal text-[32px] font-medium leading-[36px] text-left">
            Session Presenters
          </h2>
        </div>

        <div className="space-y-4 bg-white p-4 rounded border">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-font font-Roboto">
              Add New Presenter
            </h3>
          </div>

          <div className="flex gap-6 flex-wrap mb-4">
            {speakerTypeOptions.map((option, optionIndex) => {
              const presenterTypeKey =
                option.key as keyof typeof SpeakerTypeEnum;
              const isSelected = formPresenter?.personType === presenterTypeKey;
              return (
                <RadioButton
                  key={option.key}
                  index={optionIndex}
                  text={option.value}
                  selected={isSelected}
                  handleRadioButtonChange={() => {
                    setFormPresenter((prev) => ({
                      ...prev,
                      personType: presenterTypeKey,
                    }));
                  }}
                  name={`presenterType-new`}
                />
              );
            })}
          </div>

          {isGuestPresenterType ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DropDownList
                key={`presenter-company-${dropdownResetKey}`}
                title="Company"
                data={companyOptions}
                selectedObjst={formPresenter.memberFirm?.name}
                seter={(value) => {
                  if (typeof value === "string") {
                    const option = companyOptions.find(
                      (opt) => opt.name === value,
                    );
                    setFormPresenter((prev) => ({
                      ...prev,
                      memberFirm: option || null,
                      professional: null,
                    }));
                  } else if (value && value.id) {
                    setFormPresenter((prev) => ({
                      ...prev,
                      memberFirm: value,
                      professional: null,
                    }));
                  } else {
                    setFormPresenter((prev) => ({
                      ...prev,
                      memberFirm: null,
                      professional: null,
                    }));
                  }
                }}
                error={false}
                placeholder="Select Company"
                getid={true}
              />

              <DropDownList
                key={`presenter-guest-${dropdownResetKey}`}
                title="Guest Speaker"
                data={availableGuestSpeakers}
                selectedObjst={formPresenter.professional?.name}
                seter={(value) => {
                  if (typeof value === "string") {
                    const option = availableGuestSpeakers.find(
                      (opt) => opt.name === value,
                    );
                    setFormPresenter((prev) => ({
                      ...prev,
                      professional: option || null,
                    }));
                  } else if (value && value.id) {
                    setFormPresenter((prev) => ({
                      ...prev,
                      professional: value,
                    }));
                  } else {
                    setFormPresenter((prev) => ({
                      ...prev,
                      professional: null,
                    }));
                  }
                }}
                error={false}
                placeholder={
                  selectedCompanyId
                    ? "Select Guest Speaker"
                    : "Select Company first"
                }
                getid={true}
                disabled={!selectedCompanyId}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DropDownList
                  key={`presenter-firm-${dropdownResetKey}`}
                  title="Member Firm"
                  data={firmOptions}
                  selectedObjst={formPresenter.memberFirm?.name}
                  seter={(value) => {
                    if (typeof value === "string") {
                      const option = firmOptions.find(
                        (opt) => opt.name === value,
                      );
                      setFormPresenter((prev) => ({
                        ...prev,
                        selectionSource: "memberFirm",
                        memberFirm: option || null,
                        company: null,
                        professional: null,
                      }));
                    } else if (value && value.id) {
                      setFormPresenter((prev) => ({
                        ...prev,
                        selectionSource: "memberFirm",
                        memberFirm: value,
                        company: null,
                        professional: null,
                      }));
                    } else {
                      setFormPresenter((prev) => ({
                        ...prev,
                        memberFirm: null,
                        professional: null,
                        selectionSource: prev.company?.id
                          ? "company"
                          : null,
                      }));
                    }
                  }}
                  error={false}
                  placeholder="Select Member Firm"
                  getid={true}
                  disabled={isMemberFirmBlockDisabled}
                  onOpenChange={handleMemberFirmBlockOpen}
                />

                <DropDownList
                  key={`presenter-firm-professional-${dropdownResetKey}`}
                  title="Professional"
                  data={availableProfessionals}
                  selectedObjst={formPresenter.professional?.name}
                  seter={(value) => {
                    if (typeof value === "string") {
                      const option = availableProfessionals.find(
                        (opt) => opt.name === value,
                      );
                      setFormPresenter((prev) => ({
                        ...prev,
                        selectionSource: "memberFirm",
                        professional: option || null,
                      }));
                    } else if (value && value.id) {
                      setFormPresenter((prev) => ({
                        ...prev,
                        selectionSource: "memberFirm",
                        professional: value,
                      }));
                    } else {
                      setFormPresenter((prev) => ({
                        ...prev,
                        professional: null,
                      }));
                    }
                  }}
                  error={false}
                  placeholder={
                    selectedFirmId
                      ? "Select Professional"
                      : "Select Member Firm first"
                  }
                  getid={true}
                  disabled={isMemberFirmBlockDisabled || !selectedFirmId}
                  onOpenChange={handleMemberFirmBlockOpen}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DropDownList
                  key={`presenter-company-${dropdownResetKey}`}
                  title="Company"
                  data={companyOptions}
                  selectedObjst={formPresenter.company?.name}
                  seter={(value) => {
                    if (typeof value === "string") {
                      const option = companyOptions.find(
                        (opt) => opt.name === value,
                      );
                      setFormPresenter((prev) => ({
                        ...prev,
                        selectionSource: "company",
                        company: option || null,
                        memberFirm: null,
                        professional: null,
                      }));
                    } else if (value && value.id) {
                      setFormPresenter((prev) => ({
                        ...prev,
                        selectionSource: "company",
                        company: value,
                        memberFirm: null,
                        professional: null,
                      }));
                    } else {
                      setFormPresenter((prev) => ({
                        ...prev,
                        company: null,
                        professional: null,
                        selectionSource: prev.memberFirm?.id
                          ? "memberFirm"
                          : null,
                      }));
                    }
                  }}
                  error={false}
                  placeholder="Select Company"
                  getid={true}
                  disabled={isCompanyBlockDisabled}
                  onOpenChange={handleCompanyBlockOpen}
                />

                <DropDownList
                  key={`presenter-company-professional-${dropdownResetKey}`}
                  title="Professional"
                  data={availableGuestSpeakers}
                  selectedObjst={formPresenter.professional?.name}
                  seter={(value) => {
                    if (typeof value === "string") {
                      const option = availableGuestSpeakers.find(
                        (opt) => opt.name === value,
                      );
                      setFormPresenter((prev) => ({
                        ...prev,
                        selectionSource: "company",
                        professional: option || null,
                      }));
                    } else if (value && value.id) {
                      setFormPresenter((prev) => ({
                        ...prev,
                        selectionSource: "company",
                        professional: value,
                      }));
                    } else {
                      setFormPresenter((prev) => ({
                        ...prev,
                        professional: null,
                      }));
                    }
                  }}
                  error={false}
                  placeholder={
                    selectedCompanyId
                      ? "Select Professional"
                      : "Select Company first"
                  }
                  getid={true}
                  disabled={isCompanyBlockDisabled || !selectedCompanyId}
                  onOpenChange={handleCompanyBlockOpen}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <LegacyButton
              text="ADD"
              content="textIcon"
              icon={
                <div className="w-5 h-5 [&>svg>path]:fill-current ml-2">
                  <PlusIcon />
                </div>
              }
              color="accent"
              style="filled"
              size="small"
              onClick={handleSavePresenter}
            />
          </div>
        </div>

        {formatPresenterData.length > 0 && (
          <PaginatedTable
            data={formatPresenterData || []}
            columnsDef={columnsDef}
            formatData={formatDataWithSorting}
            sorting={sort || { field: "number", order: "asc" }}
            onSortingChange={setSort as any}
            totalCount={formatPresenterData.length || 0}
            pagination={pagination}
            onPaginationChange={setPagination}
            showExcelExport={false}
            showColumnVisibility={false}
          />
        )}
      </div>
    </>
  );
};

export default SessionTab;
